/**
 * Helpers around the server's TargetComparisonReport (POST /api/v1/masking/jobs/target-compare and
 * POST /api/inventories/{id}/derive-for-target) for the inventory editor and the fan-out review.
 *
 * Report shape (see platform-api TargetComparisonReport):
 *   { targetDataSourceId, targetName, skipped, skipReason, profilePending, profileJobId,
 *     snapshotMissing, snapshotTakenAt, requiredTableCount, presentTableCount,
 *     remainingMaskableTableCount, missingTables: [{schema, table, maskableColumnCount}],
 *     missingColumns: [{schema, table, column, algorithm}],
 *     constraintIssues: [{schema, table, column, algorithm, level, constraintName, message}],
 *     hasGaps, nothingToMask, compared, clean }
 * The inventory page adds UI-only `profileFailed` / `profileStatus` / `profileError` when the
 * awaited Profile ends in a non-success terminal state.
 */

const norm = (s) => String(s || '').trim().toLowerCase();

/** First "." splits schema from table, matching the accordion and the save side. */
function splitKey(tableKey) {
  const s = String(tableKey || '');
  const dot = s.indexOf('.');
  return dot > 0 ? { schema: s.slice(0, dot), table: s.slice(dot + 1) } : { schema: '', table: s };
}

/** Does a reported (schema, table) refer to this editor tableKey? Schema-lenient like the server. */
function tableMatches(entry, tableKey) {
  const { schema, table } = splitKey(tableKey);
  if (norm(entry.table) !== norm(table)) return false;
  if (!entry.schema || !schema) return true;
  return norm(entry.schema) === norm(schema);
}

/**
 * Overlay a report onto the editor's selectedFields: every field of a missing table, and every
 * missing column, is marked `targetMissing` (with `targetMissingReason: 'table' | 'column'`),
 * de-selected, and stripped of its algorithm so it can neither be masked nor re-selected. Needed
 * because the saved∪discovery merge (inventoryMerge) would otherwise resurrect rows the derived
 * inventory dropped as unsaved, recommendation-seeded rows. Returns a new object; other fields
 * are copied verbatim.
 */
export function applyTargetGaps(selectedFields, report) {
  const out = {};
  const missingTables = (report && Array.isArray(report.missingTables) ? report.missingTables : []).filter(Boolean);
  const missingColumns = (report && Array.isArray(report.missingColumns) ? report.missingColumns : []).filter(Boolean);
  for (const tableKey of Object.keys(selectedFields || {})) {
    const fields = selectedFields[tableKey] || {};
    const tableGone = missingTables.some((m) => tableMatches(m, tableKey));
    const goneColumns = new Set(
      missingColumns.filter((m) => tableMatches(m, tableKey)).map((m) => norm(m.column))
    );
    out[tableKey] = {};
    for (const field of Object.keys(fields)) {
      const fd = fields[field] || {};
      const reason = tableGone ? 'table' : goneColumns.has(norm(field)) ? 'column' : null;
      out[tableKey][field] = reason
        ? { ...fd, checked: false, algorithm: '', targetMissing: true, targetMissingReason: reason }
        : fd;
    }
  }
  return out;
}

/**
 * Undo applyTargetGaps: every row flagged `targetMissing` gets its pre-overlay state back from
 * `snapshot` (the selectedFields as they were when the overlay was applied); a row the snapshot
 * doesn't know just loses the flags. Rows the overlay never touched keep their current — possibly
 * since-edited — state. Used when the user clears or changes the mask target, and when the ROOT
 * inventory is persisted while a target preview is on screen (the overlay is preview-only).
 */
export function restoreTargetMissing(selectedFields, snapshot) {
  const out = {};
  for (const tableKey of Object.keys(selectedFields || {})) {
    const fields = selectedFields[tableKey] || {};
    out[tableKey] = {};
    for (const field of Object.keys(fields)) {
      const fd = fields[field] || {};
      if (!fd.targetMissing) {
        out[tableKey][field] = fd;
        continue;
      }
      const prev = snapshot && snapshot[tableKey] ? snapshot[tableKey][field] : null;
      if (prev) {
        out[tableKey][field] = prev;
      } else {
        const { targetMissing, targetMissingReason, ...rest } = fd;
        out[tableKey][field] = rest;
      }
    }
  }
  return out;
}

/** Remove rows flagged by applyTargetGaps before persisting a derived inventory. */
export function dropTargetMissing(selectedFields) {
  const out = {};
  for (const tableKey of Object.keys(selectedFields || {})) {
    const fields = selectedFields[tableKey] || {};
    const kept = {};
    for (const field of Object.keys(fields)) {
      const fd = fields[field];
      if (fd && fd.targetMissing) continue;
      kept[field] = fd;
    }
    if (Object.keys(kept).length > 0) out[tableKey] = kept;
  }
  return out;
}

/** Anything the user must see before running against this target. */
export function reportHasProblems(report) {
  if (!report) return false;
  if (report.skipped) return false;
  return Boolean(
    report.profilePending ||
      report.profileFailed ||
      report.snapshotMissing ||
      report.nothingToMask ||
      report.hasGaps ||
      (Array.isArray(report.constraintIssues) && report.constraintIssues.length > 0)
  );
}

/** Confirmed (will-fail) constraint issues only. */
export function confirmedConstraintIssues(report) {
  return (report && Array.isArray(report.constraintIssues) ? report.constraintIssues : []).filter(
    (i) => i && String(i.level).toUpperCase() === 'CONFIRMED'
  );
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** One-sentence description of a report for notifications and review rows. */
export function describeReport(report, targetName) {
  const name = targetName || (report && report.targetName) || 'the target';
  if (!report) return '';
  if (report.skipped) return `${name}: not compared (${report.skipReason || 'no schema comparison'})`;
  if (report.profilePending) {
    return `${name} is being profiled (job ${report.profileJobId}) — the comparison runs when it completes`;
  }
  if (report.profileFailed) {
    return `Profile of ${name} ${String(report.profileStatus || 'FAILED').toLowerCase() === 'failed' ? 'failed' : 'did not complete'}${
      report.profileError ? ` — ${report.profileError}` : ''
    }`;
  }
  if (report.snapshotMissing) return `${name} has no profiled schema — run a Profile on it first`;
  if (report.nothingToMask) return `Nothing to mask on ${name}: every inventory table is missing there`;
  const tables = Array.isArray(report.missingTables) ? report.missingTables.length : 0;
  const columns = Array.isArray(report.missingColumns) ? report.missingColumns.length : 0;
  if (!report.hasGaps) return `${name} has every table and column the inventory masks`;
  const parts = [];
  if (tables) parts.push(plural(tables, 'table', 'tables'));
  if (columns) parts.push(plural(columns, 'column', 'columns'));
  return `${parts.join(' and ')} not on ${name}`;
}

/**
 * Per-table extras a target-derived inventory must persist: `sourceColumns` for every table the
 * report restricted (the target lacks some listed column, so the worker's INSERT copy is limited
 * to the shared ones). Keyed by editor tableKey (`schema.table` or bare `table`).
 */
export function tableExtrasFromReport(report) {
  const out = {};
  const list = report && Array.isArray(report.columnRestrictions) ? report.columnRestrictions : [];
  for (const r of list) {
    if (!r || !r.table || !Array.isArray(r.sourceColumns)) continue;
    const key = r.schema ? `${r.schema}.${r.table}` : r.table;
    out[key] = { sourceColumns: r.sourceColumns.slice() };
  }
  return out;
}

/** Same shape as tableExtrasFromReport, read back from a saved inventory's content. */
export function tableExtrasFromInventoryContent(content) {
  const out = {};
  const schemas = content && Array.isArray(content.schemas) ? content.schemas : [];
  for (const s of schemas) {
    const schemaName = s && s.name ? s.name : '';
    for (const t of (s && s.tables) || []) {
      if (!t || !t.name || !Array.isArray(t.sourceColumns)) continue;
      const key = schemaName && schemaName !== 'default' ? `${schemaName}.${t.name}` : t.name;
      out[key] = { sourceColumns: t.sourceColumns.slice() };
    }
  }
  return out;
}

/** Terminal orchestration-job statuses, for waiting out a target Profile. */
export const TERMINAL_JOB_STATUSES = new Set(['SUCCEEDED', 'FAILED', 'CANCELLED', 'INTERRUPTED']);
